import { type IntlShape } from "react-intl";
import { z } from "zod";

import { machineTypeSchema } from "../machine-type";
import { getMachineRequestBaseSchema, type MachineRequestBase } from "../machine-request-base";

const x5SettingsSchema = z.object({
	printerMode: z.literal(0).or(z.literal(1)),
});

const zplBarcodeTypeCode128SubsetC = 29;

const smartScalingBarcodeGeneratorRequestSchema = z.object({
	enabled: z.boolean(),
	minBarcodeValue: z.number().int(),
	maxBarcodeValue: z.number().int(),
	barcodeType: z.number().int(),
	barcodeOrientation: z.number().int(),
	minimumPrintedBarcodeDigits: z.number().int(),
	zplBarcodeModuleWidth: z.number().int(),
	dotsPerInch: z.number().int(),
});

const printImageConfigurationRequestSchema = z.object({
	smartScalingBarcodeGenerator: smartScalingBarcodeGeneratorRequestSchema,
});

const hasEvenPrintedDigits = (value: number, minimumDigits: number): boolean => {
	const printedDigitLength = Math.max(String(value).length, minimumDigits);

	return printedDigitLength % 2 === 0;
};

export const getX5MachineRequestSchema = (intl: IntlShape, existingMachines: MachineRequestBase[] = []) => {
	const machineRequestBaseSchema = getMachineRequestBaseSchema(intl, existingMachines);

	return machineRequestBaseSchema
		.extend({
			machineType: z.literal(
				machineTypeSchema.enum.X5,
				intl.formatMessage({ id: "Machine type is invalid", defaultMessage: "Machine type is invalid" }),
			),
			settings: x5SettingsSchema,
			printerIp: z.union([machineRequestBaseSchema.shape.ipOrDnsName, z.literal(""), z.null()]),
			printerPort: z.number().int().min(1).max(65535),
			isDoubleTampPrinter: z.boolean(),
			printImageConfiguration: printImageConfigurationRequestSchema,
		})
		.superRefine((request, context) => {
			if (request.settings.printerMode === 0 && (request.printerIp === null || request.printerIp.trim() === "")) {
				context.addIssue({
					code: "custom",
					path: ["printerIp"],
					message: intl.formatMessage({
						id: "Printer IP Address is required for ZPL printers",
						defaultMessage: "Printer IP Address is required for ZPL printers",
					}),
				});
			}
		})
		.superRefine((request, context) => {
			const smartScalingBarcodeGenerator = request.printImageConfiguration.smartScalingBarcodeGenerator;
			const { minBarcodeValue, maxBarcodeValue } = smartScalingBarcodeGenerator;

			let minBarcodeValueIsValid = true;
			if (minBarcodeValue < 0) {
				minBarcodeValueIsValid = false;
				context.addIssue({
					code: "custom",
					path: ["printImageConfiguration", "smartScalingBarcodeGenerator", "minBarcodeValue"],
					message: intl.formatMessage({
						id: "Start Value must be a whole number greater than or equal to 0",
						defaultMessage: "Start Value must be a whole number greater than or equal to 0",
					}),
				});
			}

			let maxBarcodeValueIsValid = true;
			if (minBarcodeValue > maxBarcodeValue) {
				maxBarcodeValueIsValid = false;
				context.addIssue({
					code: "custom",
					path: ["printImageConfiguration", "smartScalingBarcodeGenerator", "maxBarcodeValue"],
					message: intl.formatMessage({
						id: "End Value must be greater than or equal to Start Value",
						defaultMessage: "End Value must be greater than or equal to Start Value",
					}),
				});
			}

			const { minimumPrintedBarcodeDigits, barcodeType, dotsPerInch } = smartScalingBarcodeGenerator;

			if (minimumPrintedBarcodeDigits < 1 || minimumPrintedBarcodeDigits > 10) {
				context.addIssue({
					code: "custom",
					path: ["printImageConfiguration", "smartScalingBarcodeGenerator", "minimumPrintedBarcodeDigits"],
					message: intl.formatMessage({
						id: "Must be a whole number between 1 and 10",
						defaultMessage: "Must be a whole number between 1 and 10",
					}),
				});
			}

			if (maxBarcodeValue > 2147483647) {
				maxBarcodeValueIsValid = false;
				context.addIssue({
					code: "custom",
					path: ["printImageConfiguration", "smartScalingBarcodeGenerator", "maxBarcodeValue"],
					message: intl.formatMessage({
						id: "End Value must be a whole number less than or equal to 2147483647",
						defaultMessage: "End Value must be a whole number less than or equal to 2147483647",
					}),
				});
			}

			if (dotsPerInch < 200 || dotsPerInch > 600) {
				context.addIssue({
					code: "custom",
					path: ["printImageConfiguration", "smartScalingBarcodeGenerator", "dotsPerInch"],
					message: intl.formatMessage({
						id: "Dots Per Inch must be a whole number between 200 and 600",
						defaultMessage: "Dots Per Inch must be a whole number between 200 and 600",
					}),
				});
			}

			if (barcodeType === zplBarcodeTypeCode128SubsetC) {
				if (minimumPrintedBarcodeDigits % 2 !== 0) {
					context.addIssue({
						code: "custom",
						path: ["printImageConfiguration", "smartScalingBarcodeGenerator", "minimumPrintedBarcodeDigits"],
						message: intl.formatMessage({
							id: "Code128 Subset C requires Minimum Digits to be even",
							defaultMessage: "Code128 Subset C requires Minimum Digits to be even",
						}),
					});
				}

				if (minBarcodeValueIsValid && !hasEvenPrintedDigits(minBarcodeValue, minimumPrintedBarcodeDigits)) {
					context.addIssue({
						code: "custom",
						path: ["printImageConfiguration", "smartScalingBarcodeGenerator", "minBarcodeValue"],
						message: intl.formatMessage({
							id: "Start Value must have an even number of digits for Code128 Subset C",
							defaultMessage: "Start Value must have an even number of digits for Code128 Subset C",
						}),
					});
				}

				if (maxBarcodeValueIsValid && !hasEvenPrintedDigits(maxBarcodeValue, minimumPrintedBarcodeDigits)) {
					context.addIssue({
						code: "custom",
						path: ["printImageConfiguration", "smartScalingBarcodeGenerator", "maxBarcodeValue"],
						message: intl.formatMessage({
							id: "End Value must have an even number of digits for Code128 Subset C",
							defaultMessage: "End Value must have an even number of digits for Code128 Subset C",
						}),
					});
				}
			}
		});
};

export type X5MachineRequest = z.infer<ReturnType<typeof getX5MachineRequestSchema>>;
